import {
  ChevronRight,
  ChevronDown,
  Folder,
  FileText,
  MoreHorizontal,
  Pencil,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { AccountTreeNode as AccountTreeNodeType } from "../types";

interface AccountTreeProps {
  tree: AccountTreeNodeType[];
  isExpanded: (id: string) => boolean;
  toggleExpanded: (id: string) => void;
  onEdit: (account: AccountTreeNodeType) => void;
  onCreateChild: (parent: AccountTreeNodeType) => void;
}

/**
 * Account type badge colors.
 */
const typeColors: Record<string, string> = {
  ASSET: "bg-blue-100 text-blue-800",
  LIABILITY: "bg-red-100 text-red-800",
  EQUITY: "bg-purple-100 text-purple-800",
  REVENUE: "bg-green-100 text-green-800",
  EXPENSE: "bg-orange-100 text-orange-800",
};

/**
 * Account type display names.
 */
const typeNames: Record<string, string> = {
  ASSET: "Activo",
  LIABILITY: "Pasivo",
  EQUITY: "Patrimonio",
  REVENUE: "Ingreso",
  EXPENSE: "Gasto",
};

interface TreeNodeProps {
  node: AccountTreeNodeType;
  level: number;
  isExpanded: (id: string) => boolean;
  toggleExpanded: (id: string) => void;
  onEdit: (account: AccountTreeNodeType) => void;
  onCreateChild: (parent: AccountTreeNodeType) => void;
}

/**
 * Recursive tree node component.
 */
function TreeNode({
  node,
  level,
  isExpanded,
  toggleExpanded,
  onEdit,
  onCreateChild,
}: TreeNodeProps) {
  const hasChildren = node.children.length > 0;
  const expanded = isExpanded(node.id);
  const isHeader = node.isHeader;

  return (
    <div>
      {/* Node row */}
      <div
        className={cn(
          "group flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors",
          !node.isActive && "opacity-50",
        )}
        style={{ paddingLeft: `${level * 20 + 8}px` }}
      >
        {/* Expand/collapse button */}
        <button
          onClick={hasChildren ? () => toggleExpanded(node.id) : undefined}
          className={cn(
            "w-5 h-5 flex items-center justify-center rounded",
            hasChildren ? "hover:bg-muted cursor-pointer" : "cursor-default",
          )}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : (
            <span className="w-4" />
          )}
        </button>

        {/* Icon */}
        {isHeader ? (
          <Folder className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}

        {/* Code */}
        <span className="font-mono text-sm text-muted-foreground w-20 flex-shrink-0">
          {node.code}
        </span>

        {/* Name */}
        <span
          className={cn(
            "text-sm flex-1 min-w-0 truncate",
            isHeader && "font-medium",
          )}
        >
          {node.name}
        </span>

        {/* Type badge - only at root level */}
        {level === 0 && (
          <span
            className={cn(
              "text-xs px-2 py-0.5 rounded-full flex-shrink-0",
              typeColors[node.accountType],
            )}
          >
            {typeNames[node.accountType]}
          </span>
        )}

        {/* Actions menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Acciones</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(node)}>
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onCreateChild(node)}>
              <Plus className="mr-2 h-4 w-4" />
              Agregar Subcuenta
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              isExpanded={isExpanded}
              toggleExpanded={toggleExpanded}
              onEdit={onEdit}
              onCreateChild={onCreateChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Account tree view component.
 *
 * Renders a hierarchical tree of accounts with:
 * - Expand/collapse functionality
 * - Type badges for root accounts
 * - Edit action via dropdown menu
 */
export function AccountTree({
  tree,
  isExpanded,
  toggleExpanded,
  onEdit,
  onCreateChild,
}: AccountTreeProps) {
  if (tree.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No se encontraron cuentas
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {tree.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          level={0}
          isExpanded={isExpanded}
          toggleExpanded={toggleExpanded}
          onEdit={onEdit}
          onCreateChild={onCreateChild}
        />
      ))}
    </div>
  );
}
